if (!customElements.get('wrapp-product-gallery')) {
  customElements.define(
    'wrapp-product-gallery',
    class WrappProductGallery extends HTMLElement {
      constructor() {
        super();
        this.templatesRoot = this.querySelector('.wrapp-pdp__gallery-templates');
        this.mainEl = this.querySelector('[data-gallery-main]');
        this.thumbsEl = this.querySelector('[data-gallery-thumbs]');
        this.trackEl = this.querySelector('[data-gallery-track]');
        this.dotsEl = this.querySelector('[data-gallery-dots]');
        this.statusEl = this.querySelector('[data-gallery-status]');
        this.zoomImage = document.querySelector(`#GalleryZoom-${this.dataset.section} [data-gallery-zoom-image]`);

        this.generalPrefix = this.dataset.generalPrefix || 'general';
        this.currentHandle = this.generalPrefix;
        this.debug = this.dataset.debug === 'true';

        this.modelOptionName = this.dataset.modelOptionName || '';
        this.modelInputName = this.modelOptionName
          ? `${this.modelOptionName}-${this.dataset.optionPosition}`
          : '';
        // Dawn renders the same option as a <select name="options[Name]"> when the
        // picker type is a dropdown, so accept that name too.
        this.modelSelectName = this.modelOptionName ? `options[${this.modelOptionName}]` : '';

        this.thumbsEl?.addEventListener('click', (event) => {
          const thumb = event.target.closest('.wrapp-pdp__gallery-thumb');
          if (thumb) this.setActiveThumb(Number(thumb.dataset.index));
        });

        // The gallery owns no picker of its own: it listens to the theme's
        // native variant radios/select for the Model option, plus Dawn's
        // own variantChange pub/sub as a second, defensive sync path.
        this.boundOnNativePickerChange = this.onNativePickerChange.bind(this);
        this.boundOnCustomVariantEvent = this.onCustomVariantEvent.bind(this);
      }

      connectedCallback() {
        this.indexLiveImages();
        this.buildDots();
        this.observeMobileSlides();
        this.setActiveThumb(0, { silent: true });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.dataset.section) return;
            this.onVariantChange(event.data.variant, 'variantChange pub/sub');
          });
        }

        document.addEventListener('change', this.boundOnNativePickerChange);
        document.addEventListener('variant:change', this.boundOnCustomVariantEvent);

        if (new URLSearchParams(window.location.search).has('variant')) {
          this.restoreFromUrl();
        } else {
          this.initFromNativePicker();
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.slideObserver) this.slideObserver.disconnect();
        document.removeEventListener('change', this.boundOnNativePickerChange);
        document.removeEventListener('variant:change', this.boundOnCustomVariantEvent);
      }

      // Finds the control (radio group or <select>) that drives the Model option,
      // matching the input/select name defensively (names can carry stray
      // whitespace/newlines from Liquid formatting).
      findModelControl() {
        const scope = document.querySelector(`#variant-selects-${this.dataset.section}`) || document;
        if (this.modelSelectName) {
          const select = Array.from(scope.querySelectorAll('select')).find(
            (el) => (el.name || '').trim() === this.modelSelectName
          );
          if (select) return select;
        }
        if (this.modelInputName) {
          const radio = Array.from(scope.querySelectorAll('input[type="radio"]')).find(
            (el) => (el.name || '').trim() === this.modelInputName && el.checked
          );
          if (radio) return radio;
        }
        return null;
      }

      initFromNativePicker() {
        const control = this.findModelControl();
        if (!control || !control.value) {
          this.resetToGeneral('init');
          return;
        }
        this.showGroup(this.handleize(control.value), control.value, 'init');
      }

      onNativePickerChange(event) {
        const target = event.target;
        if (!target || !target.name) return;
        const name = target.name.trim();
        const isModelControl =
          (this.modelInputName && name === this.modelInputName) ||
          (this.modelSelectName && name === this.modelSelectName);
        if (!isModelControl) return;

        if (!target.value) {
          this.resetToGeneral('native picker');
          return;
        }
        this.showGroup(this.handleize(target.value), target.value, 'native picker');
      }

      onCustomVariantEvent(event) {
        const variant = event.detail?.variant || event.detail;
        if (!variant) return;
        this.onVariantChange(variant, 'variant:change event');
      }

      restoreFromUrl() {
        const hasVariantParam = new URLSearchParams(window.location.search).has('variant');
        if (!hasVariantParam) return;
        const selectedVariantScript = document.querySelector(
          `#variant-selects-${this.dataset.section} [data-selected-variant]`
        );
        if (!selectedVariantScript) {
          this.initFromNativePicker();
          return;
        }
        try {
          const variant = JSON.parse(selectedVariantScript.textContent);
          this.onVariantChange(variant, 'url restore');
        } catch (error) {
          console.error(error);
          this.initFromNativePicker();
        }
      }

      onVariantChange(variant, source = 'variant change') {
        const modelValue = variant?.options?.[Number(this.dataset.optionPosition) - 1];
        if (!modelValue) {
          this.resetToGeneral(source);
          return;
        }
        this.showGroup(this.handleize(modelValue), modelValue, source);
      }

      resetToGeneral(source = 'reset') {
        this.showGroup(this.generalPrefix, null, source);
      }

      log(source, data) {
        if (!this.debug) return;
        console.log(`[wrapp-gallery] ${source}`, data);
      }

      handleize(value) {
        return value
          .toString()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      templatesMatching(handle) {
        return Array.from(this.templatesRoot.querySelectorAll(`template[data-alt-handle="${CSS.escape(handle)}"]`));
      }

      showGroup(handle, label, source = 'variant change') {
        const matchingTemplates = this.templatesMatching(handle);
        const matchCount = matchingTemplates.length;

        let templates = matchingTemplates;
        let usedHandle = handle;
        if (!matchCount) {
          templates = this.templatesMatching(this.generalPrefix);
          usedHandle = this.generalPrefix;
        }

        this.log(source, {
          rawValue: label ?? handle,
          handle,
          matchCount,
          shownGroup: usedHandle,
        });

        if (usedHandle === this.currentHandle) return;
        if (!templates.length) return;

        this.currentHandle = usedHandle;
        this.renderImages(templates);

        if (this.statusEl) {
          this.statusEl.textContent =
            usedHandle === this.generalPrefix ? 'Showing general images' : `Showing images for ${label || handle}`;
        }
      }

      renderImages(templates) {
        this.thumbsEl.innerHTML = '';
        this.trackEl.innerHTML = '';

        templates.forEach((template, index) => {
          const thumbButton = document.createElement('button');
          thumbButton.type = 'button';
          thumbButton.className = 'wrapp-pdp__gallery-thumb';
          thumbButton.dataset.index = String(index);
          thumbButton.setAttribute('aria-label', `Show image ${index + 1}`);
          thumbButton.appendChild(template.content.cloneNode(true));
          this.thumbsEl.appendChild(thumbButton);

          const slide = document.createElement('li');
          slide.className = 'wrapp-pdp__gallery-slide';
          slide.id = `Slide-Gallery-${this.dataset.section}-${index + 1}`;
          slide.appendChild(template.content.cloneNode(true));
          this.trackEl.appendChild(slide);
        });

        this.indexLiveImages();
        this.buildDots();
        this.setActiveThumb(0, { crossfade: true });
      }

      indexLiveImages() {
        this.thumbs = Array.from(this.thumbsEl?.querySelectorAll('.wrapp-pdp__gallery-thumb') || []);
        this.slides = Array.from(this.trackEl?.querySelectorAll('.wrapp-pdp__gallery-slide') || []);
      }

      buildDots() {
        if (!this.dotsEl) return;
        this.dotsEl.innerHTML = '';
        this.slides.forEach((slide, index) => {
          const dot = document.createElement('a');
          dot.href = `#${slide.id}`;
          dot.className = 'wrapp-pdp__gallery-dot';
          dot.setAttribute('aria-label', `Go to image ${index + 1}`);
          if (index === 0) dot.classList.add('is-active');
          this.dotsEl.appendChild(dot);
        });
      }

      observeMobileSlides() {
        if (!this.trackEl || !('IntersectionObserver' in window)) return;
        if (this.slideObserver) this.slideObserver.disconnect();

        this.slideObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const index = this.slides.indexOf(entry.target);
              const dots = this.dotsEl?.querySelectorAll('.wrapp-pdp__gallery-dot') || [];
              dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
            });
          },
          { root: this.trackEl, threshold: 0.6 }
        );

        this.slides.forEach((slide) => this.slideObserver.observe(slide));
      }

      setActiveThumb(index, { crossfade = false, silent = false } = {}) {
        if (!this.thumbs || !this.thumbs[index]) return;

        this.thumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle('is-active', thumbIndex === index));

        const sourceImg = this.thumbs[index].querySelector('img');
        if (!sourceImg) return;
        const newImg = sourceImg.cloneNode(true);
        newImg.removeAttribute('loading');
        newImg.classList.add('wrapp-pdp__gallery-main-img');

        if (this.zoomImage) {
          this.zoomImage.src = sourceImg.currentSrc || sourceImg.src;
          this.zoomImage.srcset = sourceImg.srcset || '';
          this.zoomImage.alt = sourceImg.alt || '';
        }

        const previousImg = this.mainEl.querySelector('.wrapp-pdp__gallery-main-img');

        if (!crossfade || silent || !previousImg) {
          this.mainEl.innerHTML = '';
          this.mainEl.appendChild(newImg);
          return;
        }

        newImg.classList.add('is-entering');
        this.mainEl.appendChild(newImg);
        requestAnimationFrame(() => {
          newImg.classList.add('is-visible');
          previousImg.classList.add('is-leaving');
        });
        window.setTimeout(() => {
          previousImg.remove();
          newImg.classList.remove('is-entering');
        }, 400);
      }
    }
  );
}
